import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "../../../utils/axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  FiChevronLeft,
  FiSmile,
  FiPlus,
  FiTrash2,
  FiX,
  FiImage,
  FiVideo,
  FiFile,
  FiUpload,
  FiAlertTriangle,
} from "react-icons/fi";

// ── Emoji Picker ──────────────────────────────────────────────────────────────
const EMOJIS = [
  "😀",
  "😃",
  "😄",
  "😁",
  "😆",
  "😅",
  "😂",
  "🤣",
  "😊",
  "😇",
  "🙂",
  "🙃",
  "😉",
  "😌",
  "😍",
  "🥰",
  "😘",
  "😗",
  "😙",
  "😚",
  "😋",
  "😛",
  "😝",
  "😜",
  "🤪",
  "🤨",
  "🧐",
  "🤓",
  "😎",
  "🥸",
  "🤩",
  "🥳",
  "😏",
  "😒",
  "😞",
  "😔",
  "😟",
  "😕",
  "🙁",
  "☹️",
  "😣",
  "😖",
  "😫",
  "😩",
  "🥺",
  "😢",
  "😭",
  "😤",
  "😠",
  "😡",
  "🤬",
  "🤯",
  "😳",
  "🥵",
  "🥶",
  "😱",
  "😨",
  "😰",
  "😥",
  "😓",
  "🤗",
  "🤔",
  "🤭",
  "🤫",
  "🤥",
  "😶",
  "😐",
  "😑",
  "😬",
  "🙄",
  "👍",
  "👎",
  "👏",
  "🙌",
  "❤️",
  "🧡",
  "💛",
  "💚",
  "💙",
  "💜",
  "🔥",
  "⭐",
  "✨",
  "💫",
  "🎉",
  "🎊",
  "🎁",
  "🎈",
  "💡",
  "✅",
  "❌",
  "⚠️",
  "📢",
  "📣",
  "💬",
  "📱",
  "💻",
  "🏆",
  "🚀",
  "💰",
];

function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute bottom-10 left-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-72"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-600">Emojis</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={14} />
        </button>
      </div>
      <div className="grid grid-cols-10 gap-0.5 max-h-48 overflow-y-auto">
        {EMOJIS.map((em) => (
          <button
            key={em}
            type="button"
            onClick={() => onSelect(em)}
            className="text-lg hover:bg-gray-100 rounded p-0.5 transition-colors leading-none"
          >
            {em}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Variable Warning Banner ───────────────────────────────────────────────────
function VariableWarning({ message }) {
  if (!message) return null;
  return (
    <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
      <FiAlertTriangle
        size={14}
        className="text-amber-500 mt-0.5 flex-shrink-0"
      />
      <p className="text-xs text-amber-700 leading-relaxed">{message}</p>
    </div>
  );
}

// ── Validate variable position — Meta rules ───────────────────────────────────
function getVariableWarning(text) {
  if (!text || !text.includes("{{")) return null;

  const trimmed = text.trim();

  // Check starts with variable
  if (/^\{\{\d+\}\}/.test(trimmed)) {
    return "⚠️ Variable cannot be at the START of message. Meta will reject this. Add some text before {{1}}.";
  }

  // Check ends with variable
  if (/\{\{\d+\}\}\s*$/.test(trimmed)) {
    return "⚠️ Variable cannot be at the END of message. Meta will reject this. Add some text after the variable like a period or word.";
  }

  return null;
}

// ── WhatsApp Phone Preview ────────────────────────────────────────────────────
function WhatsAppPreview({ message, formData, variables }) {
  const renderMessage = (text) => {
    if (!text) return null;
    let rendered = text;
    variables.forEach((v) => {
      rendered = rendered
        .split(`{{${v.num}}}`)
        .join(v.sample || `{{${v.num}}}`);
    });
    rendered = rendered.replace(/\*(.*?)\*/g, "<strong>$1</strong>");
    rendered = rendered.replace(/_(.*?)_/g, "<em>$1</em>");
    rendered = rendered.replace(/~(.*?)~/g, "<del>$1</del>");
    return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
  };

  const hasContent =
    formData.header ||
    message ||
    formData.footer ||
    formData.templateType === "Text with Media";

  return (
    <div className="sticky top-6">
      <div className="mb-4">
        <p className="font-semibold text-gray-800 text-base mb-2">
          Template Preview
        </p>
        <div className="space-y-1 text-xs text-gray-600">
          <p>
            Name:{" "}
            <span className="font-medium text-gray-800">
              {formData.templateName || "—"}
            </span>
          </p>
          <p>
            Language:{" "}
            <span className="font-semibold text-teal-700">
              {formData.language}
            </span>
          </p>
          <p>
            Category:{" "}
            <span className="font-semibold text-teal-700">
              {formData.category}
            </span>
          </p>
          <p>
            Type:{" "}
            <span className="font-semibold text-teal-700">
              {formData.templateType}
            </span>
          </p>
        </div>
      </div>
      <div className="relative mx-auto" style={{ width: 260 }}>
        <div className="bg-gray-900 rounded-[36px] p-2 shadow-2xl">
          <div
            className="bg-white rounded-[28px] overflow-hidden"
            style={{ height: 500 }}
          >
            <div className="bg-teal-700 px-3 py-2 flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-teal-800">
                J
              </div>
              <div>
                <p className="text-white text-xs font-semibold leading-tight">
                  jaiswal softwar...
                </p>
                <p className="text-teal-200 text-[10px]">online</p>
              </div>
            </div>
            <div
              className="p-3 flex flex-col gap-2 overflow-y-auto"
              style={{
                height: 382,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23d1fae5' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundColor: "#e5ddd5",
              }}
            >
              {hasContent ? (
                <div className="bg-white rounded-lg shadow-sm max-w-[90%] overflow-hidden self-start">
                  {formData.templateType === "Text with Media" &&
                    (formData.mediaPreview && formData.mediaType === "image" ? (
                      <img
                        src={formData.mediaPreview}
                        alt="media"
                        className="w-full max-h-28 object-cover"
                      />
                    ) : formData.mediaUrl && formData.mediaType === "image" ? (
                      <img
                        src={formData.mediaUrl}
                        alt="media"
                        className="w-full max-h-28 object-cover"
                      />
                    ) : (
                      <div className="bg-gray-100 h-20 flex items-center justify-center">
                        <div className="text-center text-gray-400">
                          {formData.mediaType === "video" ? (
                            <FiVideo size={20} className="mx-auto" />
                          ) : formData.mediaType === "document" ? (
                            <FiFile size={20} className="mx-auto" />
                          ) : (
                            <FiImage size={20} className="mx-auto" />
                          )}
                          <span className="text-[9px] mt-1 block capitalize">
                            {formData.mediaType || "Media"}
                          </span>
                        </div>
                      </div>
                    ))}
                  {formData.headerEnabled && formData.header && (
                    <div className="px-2.5 pt-2 pb-0.5">
                      <p className="text-xs font-bold text-gray-800">
                        {formData.header}
                      </p>
                    </div>
                  )}
                  {message && (
                    <div className="px-2.5 py-1.5">
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {renderMessage(message)}
                      </p>
                    </div>
                  )}
                  {formData.footerEnabled && formData.footer && (
                    <div className="px-2.5 pb-1">
                      <p className="text-[10px] text-gray-400">
                        {formData.footer}
                      </p>
                    </div>
                  )}
                  <div className="px-2.5 pb-1.5 text-right">
                    <span className="text-[9px] text-gray-300">
                      10:30 AM ✓✓
                    </span>
                  </div>
                  {formData.buttonEnabled &&
                    formData.buttons.filter((b) => b.text).length > 0 && (
                      <div className="border-t border-gray-100">
                        {formData.buttons
                          .filter((b) => b.text)
                          .map((btn, i) => (
                            <div
                              key={i}
                              className="text-center py-1.5 text-xs font-medium border-b border-gray-100 last:border-0 text-blue-500"
                            >
                              {btn.type === "url"
                                ? "🔗 "
                                : btn.type === "phone"
                                  ? "📞 "
                                  : ""}
                              {btn.text}
                            </div>
                          ))}
                      </div>
                    )}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-[11px] text-gray-400 text-center px-4">
                    Fill the form to see your template preview
                  </p>
                </div>
              )}
            </div>
            <div className="bg-gray-100 px-2 py-1.5 flex items-center gap-1.5">
              <div className="flex-1 bg-white rounded-full px-3 py-1">
                <p className="text-[10px] text-gray-300">Type a message</p>
              </div>
              <div className="w-6 h-6 bg-teal-600 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
const Toggle = ({ enabled, onToggle, readOnly }) => (
  <button
    type="button"
    onClick={readOnly ? undefined : onToggle}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-blue-500" : "bg-gray-200"} ${readOnly ? "cursor-default" : ""}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-1"}`}
    />
  </button>
);

// ── Button Input ──────────────────────────────────────────────────────────────
const BtnInput = React.memo(function BtnInput({
  value,
  placeholder,
  maxLength,
  readOnly,
  onChange,
  className,
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      readOnly={readOnly}
      onChange={onChange}
      className={
        className ||
        `w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${readOnly ? "cursor-default" : ""}`
      }
    />
  );
});

// ── Button Builder ────────────────────────────────────────────────────────────
function ButtonBuilder({ initialButtons, readOnly, onButtonsChange }) {
  const [buttons, setButtons] = useState(initialButtons || []);
  const initStr = useRef(JSON.stringify(initialButtons));

  useEffect(() => {
    const s = JSON.stringify(initialButtons);
    if (s !== initStr.current) {
      initStr.current = s;
      setButtons(initialButtons || []);
    }
  }, [initialButtons]);

  const update = useCallback(
    (newBtns) => {
      setButtons(newBtns);
      onButtonsChange(newBtns);
    },
    [onButtonsChange],
  );

  const counts = {
    quick_reply: buttons.filter((b) => b.type === "quick_reply").length,
    url: buttons.filter((b) => b.type === "url").length,
    phone: buttons.filter((b) => b.type === "phone").length,
  };

  const addButton = (type) => {
    const limits = { quick_reply: 3, url: 2, phone: 1 };
    if (counts[type] >= limits[type]) return;
    update([...buttons, { type, text: "", value: "", dynamic: false }]);
  };

  const removeButton = (idx) => update(buttons.filter((_, i) => i !== idx));

  const renderSection = (type, label, limit) => {
    const canAdd = counts[type] < limit;
    const sectionBtns = buttons
      .map((b, i) => ({ ...b, _idx: i }))
      .filter((b) => b.type === type);
    return (
      <div
        key={type}
        className="pb-4 border-b border-gray-100 last:border-0 last:pb-0"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {label}
            </span>
            <span className="text-xs text-gray-400">
              ({counts[type]}/{limit})
            </span>
          </div>
          {!readOnly && canAdd && (
            <button
              type="button"
              onClick={() => addButton(type)}
              className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-medium bg-teal-50 hover:bg-teal-100 px-2 py-1 rounded transition-colors"
            >
              <FiPlus size={11} /> Add
            </button>
          )}
        </div>
        {sectionBtns.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            {!readOnly && canAdd
              ? `Click Add to create a ${type.replace("_", " ")} button.`
              : `No ${type.replace("_", " ")} buttons.`}
          </p>
        ) : (
          <div className="space-y-2">
            {sectionBtns.map((btn) => (
              <div
                key={`${type}-${btn._idx}`}
                className="bg-gray-50 rounded-lg p-3 space-y-2"
              >
                <div className="flex gap-2 items-center">
                  <BtnInput
                    value={btn.text}
                    maxLength={25}
                    readOnly={readOnly}
                    placeholder={
                      type === "quick_reply"
                        ? "e.g. Yes, No, Learn More"
                        : type === "url"
                          ? "e.g. Visit Website"
                          : "e.g. Call Us"
                    }
                    className={`flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${readOnly ? "cursor-default" : ""}`}
                    onChange={(e) =>
                      update(
                        buttons.map((b, i) =>
                          i === btn._idx ? { ...b, text: e.target.value } : b,
                        ),
                      )
                    }
                  />
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                    {btn.text.length}/25
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeButton(btn._idx)}
                      className="text-red-400 hover:text-red-600 flex-shrink-0"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
                {type === "url" && (
                  <>
                    <BtnInput
                      value={btn.value}
                      placeholder="https://example.com"
                      readOnly={readOnly}
                      onChange={(e) =>
                        update(
                          buttons.map((b, i) =>
                            i === btn._idx
                              ? { ...b, value: e.target.value }
                              : b,
                          ),
                        )
                      }
                    />
                    {!readOnly && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={btn.dynamic || false}
                          onChange={(e) =>
                            update(
                              buttons.map((b, i) =>
                                i === btn._idx
                                  ? { ...b, dynamic: e.target.checked }
                                  : b,
                              ),
                            )
                          }
                          className="h-3.5 w-3.5 text-teal-600 rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-500">
                          Dynamic URL (append variable at end)
                        </span>
                      </label>
                    )}
                    {readOnly && btn.dynamic && (
                      <span className="text-xs text-teal-600">
                        ✓ Dynamic URL
                      </span>
                    )}
                  </>
                )}
                {type === "phone" && (
                  <BtnInput
                    value={btn.value}
                    placeholder="+91 98765 43210"
                    readOnly={readOnly}
                    onChange={(e) =>
                      update(
                        buttons.map((b, i) =>
                          i === btn._idx ? { ...b, value: e.target.value } : b,
                        ),
                      )
                    }
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-3 space-y-4">
      {renderSection("quick_reply", "Quick Reply Buttons", 3)}
      {renderSection("url", "URL Buttons", 2)}
      {renderSection("phone", "Phone Number Buttons", 1)}
      {buttons.length > 0 && (
        <p className="text-xs text-gray-400 pt-1">
          {buttons.length} button(s) total.
        </p>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CreateTemplate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isViewMode = location.pathname.includes("/view/");
  const isEditMode = location.pathname.includes("/edit/");
  const readOnly = isViewMode;

  const bodyRef = useRef(null);
  const messageRef = useRef("");
  const fileInputRef = useRef(null);
  const buttonsRef = useRef([]);

  const [previewMessage, setPreviewMessage] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [variableWarning, setVariableWarning] = useState(null); // ← NEW

  const [formData, setFormData] = useState({
    templateName: "",
    category: "Marketing",
    templateType: "Text",
    language: "English",
    headerEnabled: false,
    header: "",
    footerEnabled: false,
    footer: "",
    buttonEnabled: false,
    buttons: [],
    mediaType: "image",
    mediaFile: null,
    mediaPreview: null,
    mediaUrl: null,
  });

  const [variables, setVariables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [footerCharCount, setFooterCharCount] = useState(0);
  const [headerCharCount, setHeaderCharCount] = useState(0);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [templateStatus, setTemplateStatus] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchTemplate = async () => {
      setLoadingData(true);
      try {
        const res = await axios.get(`/api/templates/${id}`);
        if (res.data.success) {
          const t = res.data.data;
          setTemplateStatus(t.status);
          const btns = t.buttons || [];
          buttonsRef.current = btns;
          const msg = t.message || "";
          messageRef.current = msg;
          if (bodyRef.current) bodyRef.current.value = msg;
          setPreviewMessage(msg);
          setCharCount(msg.length);
          setVariableWarning(getVariableWarning(msg));
          setFormData({
            templateName: t.templateName || "",
            category: t.category || "Marketing",
            templateType: t.templateType || "Text",
            language: t.language || "English",
            headerEnabled: Boolean(t.header),
            header: t.header || "",
            footerEnabled: Boolean(t.footer),
            footer: t.footer || "",
            buttonEnabled: btns.length > 0,
            buttons: btns,
            mediaType: t.mediaType || "image",
            mediaFile: null,
            mediaPreview: null,
            mediaUrl: t.mediaUrl || null,
          });
          setHeaderCharCount((t.header || "").length);
          setFooterCharCount((t.footer || "").length);
          setVariables(t.variables || []);
        }
      } catch (err) {
        console.error("Failed to load template:", err);
        alert("Failed to load template data");
        navigate("/whatsapp/templates");
      } finally {
        setLoadingData(false);
      }
    };
    fetchTemplate();
  }, [id]);

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMessageInput = (e) => {
    if (readOnly) return;
    const val = e.target.value;
    messageRef.current = val;
    setCharCount(val.length);
    setPreviewMessage(val);
    setVariableWarning(getVariableWarning(val)); // ← REAL-TIME WARNING

    const regex = /\{\{(\d+)\}\}/g;
    const nums = new Set();
    let match;
    while ((match = regex.exec(val)) !== null) nums.add(parseInt(match[1]));
    const sorted = Array.from(nums).sort((a, b) => a - b);
    setVariables((prev) =>
      sorted.map((num) => ({
        num,
        sample: prev.find((v) => v.num === num)?.sample || "",
      })),
    );
  };

  const insertAtCursor = useCallback((text) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const current = ta.value;
    const newVal = current.substring(0, start) + text + current.substring(end);
    ta.value = newVal;
    ta.selectionStart = start + text.length;
    ta.selectionEnd = start + text.length;
    ta.focus();
    messageRef.current = newVal;
    setCharCount(newVal.length);
    setPreviewMessage(newVal);
    setVariableWarning(getVariableWarning(newVal)); // ← WARNING ON INSERT TOO
  }, []);

  const addVariable = () => {
    if (readOnly) return;
    const nextNum =
      (variables.length > 0 ? Math.max(...variables.map((v) => v.num)) : 0) + 1;
    insertAtCursor(`{{${nextNum}}}`);
    setVariables((prev) => [...prev, { num: nextNum, sample: "" }]);
  };

  const insertEmoji = useCallback(
    (emoji) => {
      insertAtCursor(emoji);
      setShowEmoji(false);
    },
    [insertAtCursor],
  );

  const applyFormat = (tag) => {
    if (readOnly) return;
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const newText =
      text.substring(0, start) +
      `${tag}${text.substring(start, end)}${tag}` +
      text.substring(end);
    ta.value = newText;
    const newCursor = end + tag.length * 2;
    ta.selectionStart = newCursor;
    ta.selectionEnd = newCursor;
    ta.focus();
    messageRef.current = newText;
    setCharCount(newText.length);
    setPreviewMessage(newText);
    setVariableWarning(getVariableWarning(newText));
  };

  const updateVariableSample = (index, value) => {
    if (readOnly) return;
    setVariables((prev) =>
      prev.map((v, i) => (i === index ? { ...v, sample: value } : v)),
    );
  };

  const handleButtonsChange = useCallback((btns) => {
    buttonsRef.current = btns;
    setFormData((prev) => ({ ...prev, buttons: btns }));
  }, []);

  const handleMediaFileChange = async (e) => {
    if (readOnly) return;
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      setFormData((prev) => ({
        ...prev,
        mediaPreview: ev.target.result,
        mediaFile: file,
      }));
    reader.readAsDataURL(file);
    setMediaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post("/api/templates/upload-media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.url)
        setFormData((prev) => ({ ...prev, mediaUrl: res.data.url }));
    } catch (err) {
      console.error("Media upload failed:", err);
    } finally {
      setMediaUploading(false);
    }
  };

  const handleSubmit = async (submitStatus) => {
    if (readOnly) return;
    const message = messageRef.current;
    if (!formData.templateName.trim())
      return alert("Template name is required!");
    if (!message.trim()) return alert("Body message is required!");

    // ── Block submission if variable warning exists and sending for approval ──
    if (submitStatus !== "draft" && variableWarning) {
      alert(
        "Please fix the variable position error before sending for approval.\n\n" +
          variableWarning,
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        templateName: formData.templateName,
        category: formData.category,
        templateType: formData.templateType,
        language: formData.language,
        header: formData.headerEnabled ? formData.header : "",
        message,
        footer: formData.footerEnabled ? formData.footer : "",
        buttons: formData.buttonEnabled ? buttonsRef.current : [],
        dynamicContent: variables.length > 0,
        variables,
        mediaType:
          formData.templateType === "Text with Media"
            ? formData.mediaType
            : null,
        mediaUrl: formData.mediaUrl || null,
        status: submitStatus === "draft" ? "Draft" : "Pending Approval",
      };
      let res;
      if (isEditMode && id) {
        res = await axios.put(`/api/templates/${id}`, payload);
      } else {
        res = await axios.post("/api/templates", payload);
      }
      if (res.data.success) navigate("/whatsapp/templates");
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isViewMode
    ? "View Template"
    : isEditMode
      ? "Edit Template"
      : "Create WhatsApp Template";

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600" />
          <span className="text-sm">Loading template...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/whatsapp/templates")}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm"
          >
            <FiChevronLeft size={16} /> Back
          </button>
          <span className="text-gray-300">|</span>
          <h1 className="text-base font-semibold text-gray-800">{pageTitle}</h1>
          {(isViewMode || isEditMode) && templateStatus && (
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                templateStatus === "Approved"
                  ? "bg-green-100 text-green-700"
                  : templateStatus === "Pending Approval"
                    ? "bg-yellow-100 text-yellow-700"
                    : templateStatus === "Rejected"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-600"
              }`}
            >
              {templateStatus}
            </span>
          )}
        </div>
        {isViewMode && (
          <button
            onClick={() => navigate(`/whatsapp/templates/edit/${id}`)}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Edit Template
          </button>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 flex gap-6">
        <div className="flex-1 min-w-0 space-y-4">
          {/* Card 1 — Template Info */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-teal-700 text-white px-4 py-2.5 flex items-center justify-between">
              <span className="text-sm font-semibold">
                {isViewMode
                  ? "Template Info"
                  : isEditMode
                    ? "Edit Template"
                    : "Create Template"}
              </span>
              <span className="text-lg leading-none">−</span>
            </div>
            <div className="p-4 space-y-4">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Template Name{" "}
                  {!readOnly && (
                    <span className="text-xs text-gray-400">
                      (Ex: template_name)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleChange}
                  readOnly={readOnly}
                  placeholder="template_name"
                  className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none ${!readOnly ? "focus:ring-2 focus:ring-teal-500" : "bg-gray-50 cursor-default"}`}
                />
              </div>
              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Language
                </label>
                {readOnly ? (
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {formData.language}
                  </p>
                ) : (
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    <option>English</option>
                    <option>Hindi</option>
                    <option>Gujarati</option>
                    <option>Marathi</option>
                    <option>Tamil</option>
                    <option>Telugu</option>
                  </select>
                )}
              </div>
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Category
                </label>
                {readOnly ? (
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {formData.category}
                  </p>
                ) : (
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    <option>Marketing</option>
                    <option>Utility</option>
                  </select>
                )}
              </div>
              {/* Template Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Template Type
                </label>
                {readOnly ? (
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {formData.templateType}
                  </p>
                ) : (
                  <select
                    name="templateType"
                    value={formData.templateType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    <option>Text</option>
                    <option>Text with Media</option>
                  </select>
                )}
              </div>
              {/* Media */}
              {formData.templateType === "Text with Media" && (
                <div className="border border-dashed border-gray-200 rounded-xl p-4 bg-gray-50">
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    Media
                  </p>
                  {readOnly ? (
                    <div className="text-center">
                      {formData.mediaUrl && formData.mediaType === "image" ? (
                        <img
                          src={formData.mediaUrl}
                          alt="template media"
                          className="mx-auto max-h-48 rounded-lg object-contain border border-gray-200"
                        />
                      ) : formData.mediaUrl ? (
                        <div className="flex flex-col items-center text-teal-600 py-4">
                          {formData.mediaType === "video" ? (
                            <FiVideo size={36} />
                          ) : (
                            <FiFile size={36} />
                          )}
                          <p className="text-sm text-gray-600 mt-2 capitalize">
                            {formData.mediaType} file
                          </p>
                          <a
                            href={formData.mediaUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 text-xs text-teal-600 underline"
                          >
                            View file
                          </a>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          No media uploaded
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-2 capitalize">
                        Type: {formData.mediaType}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-3">
                        {[
                          { type: "image", Icon: FiImage, label: "Image" },
                          { type: "video", Icon: FiVideo, label: "Video" },
                          { type: "document", Icon: FiFile, label: "Document" },
                        ].map(({ type, Icon, label }) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                mediaType: type,
                                mediaFile: null,
                                mediaPreview: null,
                                mediaUrl: null,
                              }))
                            }
                            className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-xs font-medium transition-all ${formData.mediaType === type ? "border-teal-500 bg-teal-50 text-teal-700 shadow-sm" : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"}`}
                          >
                            <Icon size={18} />
                            {label}
                          </button>
                        ))}
                      </div>
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-white transition-all"
                      >
                        {formData.mediaPreview &&
                        formData.mediaType === "image" ? (
                          <div className="relative inline-block">
                            <img
                              src={formData.mediaPreview}
                              alt="preview"
                              className="mx-auto max-h-36 rounded-lg object-contain"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormData((prev) => ({
                                  ...prev,
                                  mediaPreview: null,
                                  mediaFile: null,
                                  mediaUrl: null,
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow"
                            >
                              <FiX size={11} />
                            </button>
                          </div>
                        ) : formData.mediaFile ? (
                          <div className="flex flex-col items-center text-teal-600">
                            {formData.mediaType === "video" ? (
                              <FiVideo size={32} />
                            ) : (
                              <FiFile size={32} />
                            )}
                            <p className="text-sm text-gray-700 mt-2 font-medium">
                              {formData.mediaFile.name}
                            </p>
                            {mediaUploading ? (
                              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-400">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-500" />{" "}
                                Uploading...
                              </div>
                            ) : (
                              formData.mediaUrl && (
                                <p className="text-xs text-green-600 mt-1">
                                  ✓ Uploaded successfully
                                </p>
                              )
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center text-gray-400">
                            <FiUpload size={26} className="mb-2" />
                            <p className="text-sm font-medium text-gray-600">
                              Click to upload {formData.mediaType}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formData.mediaType === "image"
                                ? "JPG, PNG, WEBP · max 5MB"
                                : formData.mediaType === "video"
                                  ? "MP4 · max 16MB"
                                  : "PDF, DOC · max 100MB"}
                            </p>
                          </div>
                        )}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept={
                          formData.mediaType === "image"
                            ? "image/*"
                            : formData.mediaType === "video"
                              ? "video/*"
                              : ".pdf,.doc,.docx"
                        }
                        onChange={handleMediaFileChange}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Card 2 — Template Details */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-teal-700 text-white px-4 py-2.5 flex items-center justify-between">
              <span className="text-sm font-semibold">Template Details</span>
              <span className="text-lg leading-none">−</span>
            </div>
            <div className="p-4 space-y-4">
              {/* Header */}
              {formData.templateType === "Text" && (
                <div className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        Header{" "}
                        <span className="text-xs text-gray-400 font-normal">
                          (optional)
                        </span>
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Add a title text to the top of your message.
                      </p>
                    </div>
                    <Toggle
                      enabled={formData.headerEnabled}
                      readOnly={readOnly}
                      onToggle={() =>
                        setFormData((prev) => ({
                          ...prev,
                          headerEnabled: !prev.headerEnabled,
                          header: "",
                        }))
                      }
                    />
                  </div>
                  {formData.headerEnabled && (
                    <div className="mt-3">
                      <input
                        type="text"
                        value={formData.header}
                        readOnly={readOnly}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            header: e.target.value,
                          }));
                          setHeaderCharCount(e.target.value.length);
                        }}
                        placeholder="Enter Header Text"
                        maxLength={60}
                        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none ${!readOnly ? "focus:ring-2 focus:ring-teal-500" : "bg-gray-50 cursor-default"}`}
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">
                        Character : {headerCharCount}/60
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Body */}
              <div className="border border-gray-100 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Body Message{" "}
                  {!readOnly && <span className="text-red-500">*</span>}
                </p>
                <textarea
                  ref={bodyRef}
                  defaultValue=""
                  onInput={handleMessageInput}
                  readOnly={readOnly}
                  rows={5}
                  placeholder="Enter your message here..."
                  maxLength={1024}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none resize-none transition-colors ${
                    variableWarning && !readOnly
                      ? "border-amber-400 focus:ring-2 focus:ring-amber-400"
                      : !readOnly
                        ? "border-gray-200 focus:ring-2 focus:ring-teal-500"
                        : "border-gray-200 bg-gray-50 cursor-default"
                  }`}
                />

                {/* ── Variable Warning ── */}
                {!readOnly && <VariableWarning message={variableWarning} />}

                {!readOnly && (
                  <div className="flex items-center justify-between mt-1.5 flex-wrap gap-2">
                    <div className="flex items-center gap-0.5 flex-wrap">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowEmoji((v) => !v)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-500"
                        >
                          <FiSmile size={14} />
                        </button>
                        {showEmoji && (
                          <EmojiPicker
                            onSelect={insertEmoji}
                            onClose={() => setShowEmoji(false)}
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => applyFormat("*")}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-700 font-bold text-xs"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormat("_")}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-700 italic text-xs"
                      >
                        I
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormat("~")}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-700 text-xs line-through"
                      >
                        S
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormat("`")}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-600 font-mono text-xs"
                      >
                        &lt;/&gt;
                      </button>
                      <div className="w-px h-4 bg-gray-200 mx-1" />
                      <button
                        type="button"
                        onClick={addVariable}
                        className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                      >
                        Add Variable
                      </button>
                    </div>
                    <span className="text-xs text-gray-400">
                      Character : {charCount}/1024
                    </span>
                  </div>
                )}

                {variables.length > 0 && (
                  <div className="mt-3 bg-amber-50 border border-amber-100 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-medium text-amber-700">
                      {readOnly
                        ? "Variable sample data"
                        : "Please add sample data for variables"}
                    </p>
                    {variables.map((v, i) => (
                      <div
                        key={`var-${v.num}`}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xs bg-white border border-amber-200 text-gray-600 px-2.5 py-1.5 rounded-lg font-mono whitespace-nowrap flex-shrink-0">{`{{${v.num}}}`}</span>
                        <input
                          type="text"
                          placeholder={`Sample Data of {{${v.num}}}`}
                          value={v.sample}
                          readOnly={readOnly}
                          onChange={(e) =>
                            updateVariableSample(i, e.target.value)
                          }
                          className={`flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none bg-white ${!readOnly ? "focus:ring-1 focus:ring-teal-400" : "cursor-default bg-gray-50"}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Footer{" "}
                      <span className="text-xs text-gray-400 font-normal">
                        (optional)
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Add a short line of text to the bottom of your message.
                    </p>
                  </div>
                  <Toggle
                    enabled={formData.footerEnabled}
                    readOnly={readOnly}
                    onToggle={() =>
                      setFormData((prev) => ({
                        ...prev,
                        footerEnabled: !prev.footerEnabled,
                        footer: "",
                      }))
                    }
                  />
                </div>
                {formData.footerEnabled && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={formData.footer}
                      readOnly={readOnly}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          footer: e.target.value,
                        }));
                        setFooterCharCount(e.target.value.length);
                      }}
                      placeholder="Enter footer message"
                      maxLength={60}
                      className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none ${!readOnly ? "focus:ring-2 focus:ring-teal-500" : "bg-gray-50 cursor-default"}`}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      Character : {footerCharCount}/60
                    </p>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Button{" "}
                      <span className="text-xs text-gray-400 font-normal">
                        (optional)
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Mix quick reply, URL & phone buttons simultaneously.
                    </p>
                  </div>
                  <Toggle
                    enabled={formData.buttonEnabled}
                    readOnly={readOnly}
                    onToggle={() => {
                      const newEnabled = !formData.buttonEnabled;
                      if (!newEnabled) buttonsRef.current = [];
                      setFormData((prev) => ({
                        ...prev,
                        buttonEnabled: newEnabled,
                        buttons: newEnabled ? prev.buttons : [],
                      }));
                    }}
                  />
                </div>
                {formData.buttonEnabled && (
                  <ButtonBuilder
                    initialButtons={formData.buttons}
                    readOnly={readOnly}
                    onButtonsChange={handleButtonsChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {!readOnly && (
            <div className="flex justify-center gap-3 pb-6">
              <button
                type="button"
                onClick={() => handleSubmit("draft")}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-50 shadow-sm transition-colors"
              >
                💾 Save Draft
              </button>
              <button
                type="button"
                onClick={() => handleSubmit("approve")}
                disabled={saving || !!variableWarning}
                title={
                  variableWarning ? "Fix variable position error first" : ""
                }
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium shadow-sm transition-colors text-white
                  ${variableWarning ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}
                  disabled:opacity-50`}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />{" "}
                    Saving...
                  </>
                ) : isEditMode ? (
                  "Update & Send For Approval 📤"
                ) : (
                  "Save & Send For Approval 📤"
                )}
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Preview */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <WhatsAppPreview
            message={previewMessage}
            formData={formData}
            variables={variables}
          />
        </div>
      </div>
    </div>
  );
}
